import { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: { sitekey: string; callback: (token: string) => void; size?: string },
      ) => string;
      remove: (id: string) => void;
    };
  }
}

interface Props {
  siteKey: string;
  onSolve: (token: string) => void;
  className?: string;
}

/**
 * Cloudflare Turnstile (managed/invisible). Renders into a container the widget
 * targets; calls onSolve with the token. Script is injected once per page.
 */
export function TurnstileWidget({ siteKey, onSolve, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    function renderWidget() {
      if (cancelled || !ref.current || !window.turnstile) return;
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        size: 'flexible',
        callback: onSolve,
      });
    }
    if (window.turnstile) {
      renderWidget();
    } else if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = renderWidget;
      document.head.appendChild(s);
    } else {
      document
        .querySelector(`script[src="${SCRIPT_SRC}"]`)
        ?.addEventListener('load', renderWidget, { once: true });
    }
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [siteKey, onSolve]);

  return <div ref={ref} data-sitekey={siteKey} className={className} />;
}
